import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Search, X } from "lucide-react";
import { ButtonLoader, SectionLoader } from "../../component/common/Loader";
import EmptyScreen from "../../component/common/EmptyScreen";
import TakeawayOrderCard from "./TakeawayOrderCard";
import TakeawayBillingModal from "./TakeawayBillingModal";
import { getTakeawayOrders, updateTakeawayStatus } from "./takeawayApi";
import "./takeaway.css";

const titles = { live: "Live Takeaway Orders", history: "Takeaway History", cancelled: "Cancelled Takeaway Orders" };

const TakeawayOrders = ({ view = "live" }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [billingOrder, setBillingOrder] = useState(null);
  const [tokenSearch, setTokenSearch] = useState("");
  const realtimeRevision = useSelector((state) => state.notifications?.takeawayRevision || 0);

  const filteredOrders = useMemo(() => {
    const query = tokenSearch.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) => String(order.tokenNumber || "").toLowerCase().includes(query));
  }, [orders, tokenSearch]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { setOrders(await getTakeawayOrders(view)); }
    catch (error) { if (!silent) toast.error(error.message); }
    finally { if (!silent) setLoading(false); }
  }, [view]);

  useEffect(() => {
    load();
    if (view !== "live") return undefined;
    const timer = setInterval(() => load(true), 15000);
    return () => clearInterval(timer);
  }, [load, view, realtimeRevision]);

  const changeStatus = async (order, status) => {
    if (status >= 5 && !window.confirm(`Confirm ${status === 5 ? "cancellation" : "rejection"} of ${order.tokenNumber}?`)) return;
    setBusy(order.takeawayOrderId);
    try {
      await updateTakeawayStatus(order.takeawayOrderId, status);
      toast.success(`${order.tokenNumber} updated.`);
      await load(true);
    } catch (error) { toast.error(error.message); }
    finally { setBusy(""); }
  };

  return <div className="takeaway-page">
    <div className="takeaway-title"><div><h1>{titles[view]}</h1><p>{view === "live" ? "Auto-refreshes every 15 seconds" : "Latest 250 orders"}</p></div><div className="takeaway-actions"><button onClick={() => load()} disabled={loading} aria-busy={loading}>{loading ? <ButtonLoader label="Refreshing..." /> : "Refresh"}</button>{view === "live" && <Link to="/takeaway/new">New order</Link>}</div></div>
    <div className="takeaway-order-toolbar">
      <div className="takeaway-filter-copy">
        <label htmlFor={`takeaway-token-search-${view}`}>Search orders</label>
        <span>Enter a full or partial token number</span>
      </div>
      <div className="takeaway-token-search">
        <Search size={18} aria-hidden="true" />
        <input
          id={`takeaway-token-search-${view}`}
          type="search"
          aria-label="Search orders by token"
          placeholder="Search by token"
          value={tokenSearch}
          onChange={(event) => setTokenSearch(event.target.value)}
        />
        {tokenSearch && <button type="button" aria-label="Clear token search" onClick={() => setTokenSearch("")}><X size={17} /></button>}
      </div>
      {!loading && <span className="takeaway-filter-count">{tokenSearch.trim() ? `${filteredOrders.length} ${filteredOrders.length === 1 ? "order" : "orders"} found` : `${orders.length} ${orders.length === 1 ? "order" : "orders"}`}</span>}
    </div>
    {loading ? <SectionLoader label="Loading orders..." rows={4} /> : orders.length === 0 ? <EmptyScreen message={`No ${view} takeaway orders yet.`} subText="New orders will appear here automatically." /> : filteredOrders.length === 0 ? <EmptyScreen message="No matching token" subText="Try another full or partial token number." actionLabel="Clear search" onAction={() => setTokenSearch("")} /> : <div className="takeaway-order-grid">{filteredOrders.map((order) => <TakeawayOrderCard key={order.takeawayOrderId} order={order} onStatus={changeStatus} onBilling={setBillingOrder} busy={busy === order.takeawayOrderId} readonly={view !== "live"} />)}</div>}
    {billingOrder && <TakeawayBillingModal order={billingOrder} onClose={() => setBillingOrder(null)} onUpdated={() => load(true)} />}
  </div>;
};

export default TakeawayOrders;
